import React, { useState, useEffect, useRef, useMemo } from 'react'

/* ─────────────────────────────────────────────────────────────
   Partner Hotels — Service Request Form

   Geographic data:
   • Countries — fetched once from russ666/all-countries-and-cities-json
     (GitHub raw, no API key, ~250 countries, module-level cache)
   • Cities    — derived from the same payload, keyed by country name.
     Fetched once for the whole dataset; cities for the selected country
     are extracted in memory instantly with no second request.
───────────────────────────────────────────────────────────── */

const GEO_URL =
  'https://raw.githubusercontent.com/russ666/all-countries-and-cities-json/master/countries.json'

// Module-level cache — { "France": ["Paris", "Lyon", ...], ... }
let geoCache = null

function useGeoData() {
  const [data,    setData]    = useState(geoCache || null)
  const [loading, setLoading] = useState(!geoCache)

  useEffect(() => {
    if (geoCache) { setLoading(false); return }

    fetch(GEO_URL)
      .then(r => r.json())
      .then(json => {
        geoCache = json
        setData(json)
        setLoading(false)
      })
      .catch(() => {
        geoCache = {}
        setData({})
        setLoading(false)
      })
  }, [])

  return { data, loading }
}

/* ── Sorted country names ── */
function useCountries(geoData) {
  return useMemo(() => {
    if (!geoData) return []
    return Object.keys(geoData).sort((a, b) => a.localeCompare(b))
  }, [geoData])
}

/* ── Cities for a given country ── */
function useCities(geoData, countryName) {
  return useMemo(() => {
    if (!geoData || !countryName) return []
    const list = geoData[countryName]
    if (!Array.isArray(list)) return []
    return [...list].sort((a, b) => a.localeCompare(b))
  }, [geoData, countryName])
}

/* ─────────────────────────────────────────────────────────────
   SEARCHABLE SELECT — used for both Country and City
   Shows an input that filters a dropdown list.
───────────────────────────────────────────────────────────── */
function SearchableSelect({ id, label, placeholder, options, value, onChange, disabled, loading }) {
  const [query,  setQuery]  = useState(value || '')
  const [open,   setOpen]   = useState(false)
  const inputRef            = useRef(null)

  // Keep query in sync if value is cleared externally (e.g. country change resets city)
  useEffect(() => {
    if (!value) setQuery('')
  }, [value])

  const filtered = useMemo(() => {
    if (!query || query.length < 1) return options.slice(0, 100) // cap initial list
    const q = query.toLowerCase()
    return options.filter(o => o.toLowerCase().includes(q)).slice(0, 80)
  }, [options, query])

  const select = (option) => {
    setQuery(option)
    onChange(option)
    setOpen(false)
  }

  const handleInputChange = (e) => {
    setQuery(e.target.value)
    onChange('')   // clear confirmed value when user types
    setOpen(true)
  }

  const showLoading   = open && loading
  const showResults   = open && !loading && filtered.length > 0
  const showNoResults = open && !loading && query.length >= 1 && filtered.length === 0

  return (
    <div className="svc-field">
      <label className="svc-label" htmlFor={id}>{label}</label>
      <div className="svc-airport-wrap">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={`svc-input${disabled ? ' svc-input--disabled' : ''}`}
          value={query}
          placeholder={loading ? 'Loading…' : placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
        />

        {showLoading && (
          <ul className="svc-airport-dropdown">
            <li className="svc-airport-option svc-airport-option--muted">Loading…</li>
          </ul>
        )}

        {showResults && (
          <ul className="svc-airport-dropdown">
            {filtered.map(option => (
              <li key={option}>
                <button
                  type="button"
                  className="svc-airport-option svc-airport-option--geo"
                  onMouseDown={() => select(option)}
                >
                  {option}
                </button>
              </li>
            ))}
          </ul>
        )}

        {showNoResults && (
          <ul className="svc-airport-dropdown">
            <li className="svc-airport-option svc-airport-option--muted">
              No results for "{query}"
            </li>
          </ul>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────── */
const ROOM_PREFERENCES = [
  { id: 'suite',     label: 'Suite'            },
  { id: 'connect',   label: 'Connecting Rooms' },
  { id: 'highfloor', label: 'High Floor'       },
  { id: 'privacy',   label: 'Complete Privacy' },
]

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function PartnerHotels({ onBack, member }) {
  // Geographic data
  const { data: geoData, loading: geoLoading } = useGeoData()
  const countries = useCountries(geoData)

  const [submitted,   setSubmitted]   = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [country,     setCountry]     = useState('')
  const [city,        setCity]        = useState('')
  const [checkIn,     setCheckIn]     = useState('')
  const [checkOut,    setCheckOut]    = useState('')
  const [guests,      setGuests]      = useState(1)
  const [roomPrefs,   setRoomPrefs]   = useState([])
  const [specialReqs, setSpecialReqs] = useState('')

  const cities = useCities(geoData, country)

  // When country changes, reset city
  const handleCountryChange = (val) => {
    setCountry(val)
    setCity('')
  }

  const togglePref = (id) =>
    setRoomPrefs(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    )

  const nights = () => {
    if (!checkIn || !checkOut) return null
    const diff = (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)
    return diff > 0 ? diff : null
  }

  const destinationLabel = () => {
    if (city && country) return `${city}, ${country}`
    if (country) return country
    return '—'
  }

  const handleSubmit  = (e) => { e.preventDefault(); setShowConfirm(true) }
  const handleConfirm = ()  => { setShowConfirm(false); setSubmitted(true) }

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">Request Received</h2>
          <p className="svc-submitted__body">
            Your request has been received. We will be in touch within the hour.
          </p>
          <p className="svc-submitted__sig">— Mwuïlu</p>
          <button className="svc-btn-ghost" onClick={onBack}>Return to Dashboard</button>
        </div>
      </div>
    )
  }

  /* ── Confirmation modal ── */
  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">Please review your request</p>
          <h2 className="svc-confirm__heading">Partner Hotels</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label="Country"     value={country   || '—'} />
            <ConfirmRow label="City"        value={city      || '—'} />
            <ConfirmRow label="Check-in"    value={checkIn   || '—'} />
            <ConfirmRow label="Check-out"   value={checkOut  || '—'} />
            {nights() && (
              <ConfirmRow label="Duration" value={`${nights()} night${nights() !== 1 ? 's' : ''}`} />
            )}
            <ConfirmRow label="Guests" value={`${guests} guest${guests !== 1 ? 's' : ''}`} />
            {roomPrefs.length > 0 && (
              <ConfirmRow
                label="Room preferences"
                value={roomPrefs
                  .map(id => ROOM_PREFERENCES.find(r => r.id === id)?.label)
                  .join(', ')}
              />
            )}
            {specialReqs && <ConfirmRow label="Special requirements" value={specialReqs} />}
          </div>
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>Edit</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>Confirm &amp; Submit</button>
          </div>
        </div>
      </div>
    )
  }

  /* ── Form ── */
  return (
    <div className="db-page svc-page">

      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label="Back to dashboard">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">Service Request</p>
          <h1 className="svc-header__title">Partner Hotels</h1>
        </div>
        <p className="svc-header__note">Partner properties only. No third-party aggregators.</p>
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Destination ── */}
        <div className="svc-section">
          <p className="svc-section__label">Destination</p>
          <div className="svc-row-2">

            {/* Country — searchable, always enabled */}
            <SearchableSelect
              id="country"
              label="Country"
              placeholder="Search country…"
              options={countries}
              value={country}
              onChange={handleCountryChange}
              loading={geoLoading}
              disabled={false}
            />

            {/* City — searchable, disabled until a country is confirmed */}
            <SearchableSelect
              id="city"
              label="City"
              placeholder={country ? 'Search city…' : 'Select a country first'}
              options={cities}
              value={city}
              onChange={setCity}
              loading={false}
              disabled={!country}
            />

          </div>
          {country && cities.length === 0 && !geoLoading && (
            <p className="svc-inline-note" style={{ marginTop: '8px' }}>
              No cities on file for {country} — please specify in Special Requirements.
            </p>
          )}
        </div>

        {/* ── Dates ── */}
        <div className="svc-section">
          <p className="svc-section__label">Dates</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="checkIn">Check-in</label>
              <input
                id="checkIn"
                type="date"
                className="svc-input"
                value={checkIn}
                onChange={e => setCheckIn(e.target.value)}
                required
              />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="checkOut">Check-out</label>
              <input
                id="checkOut"
                type="date"
                className="svc-input"
                value={checkOut}
                onChange={e => setCheckOut(e.target.value)}
                required
              />
            </div>
          </div>
          {nights() && (
            <p className="svc-inline-note">{nights()} night{nights() !== 1 ? 's' : ''}</p>
          )}
        </div>

        {/* ── Guests ── */}
        <div className="svc-section">
          <p className="svc-section__label">Number of Guests</p>
          <div className="svc-steppers">
            <div className="svc-stepper">
              <span className="svc-stepper__label">Guests</span>
              <div className="svc-stepper__controls">
                <button
                  type="button"
                  className="svc-stepper__btn"
                  onClick={() => setGuests(g => Math.max(1, g - 1))}
                  disabled={guests <= 1}
                  aria-label="Decrease guests"
                >−</button>
                <span className="svc-stepper__value">{guests}</span>
                <button
                  type="button"
                  className="svc-stepper__btn"
                  onClick={() => setGuests(g => Math.min(20, g + 1))}
                  aria-label="Increase guests"
                >+</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Room preferences ── */}
        <div className="svc-section">
          <p className="svc-section__label">Room Preferences</p>
          <div className="svc-checkbox-grid">
            {ROOM_PREFERENCES.map(pref => (
              <label key={pref.id} className="svc-checkbox-label">
                <input
                  type="checkbox"
                  className="svc-checkbox"
                  checked={roomPrefs.includes(pref.id)}
                  onChange={() => togglePref(pref.id)}
                />
                <span>{pref.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ── Special requirements ── */}
        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="hotelSpecial">Special Requirements</label>
            <textarea
              id="hotelSpecial"
              className="svc-textarea"
              rows={3}
              value={specialReqs}
              onChange={e => setSpecialReqs(e.target.value)}
              placeholder="Accessibility needs, arrival time, specific requests…"
            />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">
            Review Request
          </button>
        </div>

      </form>
    </div>
  )
}